import { cn } from '@/shadcn-ui/utils';
import { Drawer as DrawerPrimitive } from 'vaul';
import * as React from 'react';
import CancelWithCircle, { CancelCircleProps } from '@/icons/cancel-with-circle';

const Drawer = ({
  shouldScaleBackground = false,
  preventScrollRestoration = false,
  dismissible = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    preventScrollRestoration={preventScrollRestoration}
    dismissible={dismissible}
    {...props}
  />
);
Drawer.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-50 bg-black/65', className)}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

interface DrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> {
  className?: string;
  showCloseIcon?: boolean;
  closeIconPropConfiguration?: CancelCircleProps;
  removeContentPadding?: boolean;
}

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  DrawerContentProps
>(
  (
    {
      className,
      showCloseIcon = true,
      closeIconPropConfiguration,
      children,
      removeContentPadding = false,
      ...props
    },
    ref
  ) => (
    <DrawerPortal>
      <DrawerOverlay />
      <DrawerPrimitive.Content
        ref={ref}
        className={cn(
          'fixed inset-x-0 bottom-0 left-0 right-0 z-50 mt-24 flex h-auto max-h-[calc(100%-24px)] flex-col rounded-t-[24px] border bg-background',
          className
        )}
        {...props}
      >
        <div
          className={cn(
            'scrollbar-hide relative mx-auto flex w-full flex-col overflow-auto',
            {
              'p-[24px]': !removeContentPadding,
            }
          )}
        >
          {children}
          {showCloseIcon ? (
            <DrawerPrimitive.Close className="absolute right-4 top-4 rounded-full focus:outline-none disabled:pointer-events-none">
              <CancelWithCircle {...closeIconPropConfiguration} />
              <span className="sr-only">Close</span>
            </DrawerPrimitive.Close>
          ) : null}
        </div>
      </DrawerPrimitive.Content>
    </DrawerPortal>
  )
);
DrawerContent.displayName = 'DrawerContent';

interface DrawerHeaderProps {
  className?: string;
  children: React.ReactNode;
}

const DrawerHeader = ({ className, children, ...props }: DrawerHeaderProps) => (
  <div
    className={cn('mt-8 flex flex-col space-y-1.5 text-center sm:text-left', className)}
    {...props}
  >
    {children}
  </div>
);
DrawerHeader.displayName = 'DrawerHeader';

interface DrawerFooterProps {
  className?: string;
}

const DrawerFooter = ({ className, ...props }: DrawerFooterProps) => (
  <div className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title ref={ref} className={className} {...props} />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
